import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useTheme } from "@/contexts/ThemeContext";
import { Bell, AlertTriangle, CheckCircle, X, Trash2, Clock, RefreshCw } from "lucide-react";
import apiService from "@/services/api";
import { toast } from "sonner";

interface Notification {
  _id?: string;
  id: string;
  body?: string;
  document?: string;

  createdAt?: string;
  updatedAt?: string;
  title: string;
  message?: string;
  type: "info" | "success" | "warning" | "error";
  timestamp: string;
  read: boolean;
  category: "system" | "user" | "admin" | "alert";
}

export function Notifications() {
  const { currentTheme } = useTheme();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    email: true,
    push: true,
    sms: false,
    system: true,
    user: true,
    admin: true,
    alert: true,
  });

  const defaultSettings = {
    email: true,
    push: true,
    sms: false,
    system: true,
    user: true,
    admin: true,
    alert: true,
  };

  // Load notifications from API
  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await apiService.getAllNotifications();
      if (response.type === 0) {
        // Transform API data to match our interface
        const apiNotifications = response.data.history.map((item: unknown) => {
          const notificationItem = item as {
            _id?: string;
            id?: string;
            title?: string;
            body?: string;
            message?: string;
            createdAt?: string;
          };
          return {
            id: notificationItem._id ?? notificationItem.id ?? "unknown",
            title: notificationItem.title ?? "Notification",
            message: notificationItem.body ?? notificationItem.message ?? "No message",
            type: "info" as const,
            timestamp: notificationItem.createdAt
              ? new Date(notificationItem.createdAt).toLocaleDateString()
              : "Unknown",
            read: false,
            category: "system" as const,
          };
        });
        setNotifications(apiNotifications);
      } else {
        toast.error(response.message || "Failed to load notifications");
        setNotifications([]);
      }
    } catch (error) {
      console.error("Error loading notifications:", error);
      toast.error("Failed to load notifications");
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  // Load notification settings
  const loadNotificationSettings = async () => {
    try {
      const response = await apiService.getAdminSettings();
      if (response.type === 0 && response.data?.preferences?.notifications) {
        setNotificationSettings(prev => ({
          ...prev,
          email: response.data.preferences.notifications.email ?? true,
          push: response.data.preferences.notifications.push ?? true,
          sms: response.data.preferences.notifications.sms ?? false,
        }));
      }
    } catch (error) {
      console.error("Error loading notification settings:", error);
    }
  };

  useEffect(() => {
    void loadNotifications();
    void loadNotificationSettings();
  }, []);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "warning":
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case "error":
        return <X className="w-5 h-5 text-red-500" />;
      default:
        return <Bell className="w-5 h-5 text-blue-500" />;
    }
  };

  const getNotificationBadge = (type: string) => {
    switch (type) {
      case "success":
        return "bg-green-100 text-green-800";
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      case "error":
        return "bg-red-100 text-red-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  const markAsRead = async (id: string) => {
    try {
      // Update local state immediately for better UX
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === id ? { ...notification, read: true } : notification
        )
      );

      // Optionally call API to mark as read on server
      // await apiService.updateNotification(id, { read: true });
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      const response = await apiService.deleteNotification(id);
      if (response.type === 0) {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
        toast.success("Notification deleted successfully");
      } else {
        toast.error(response.message || "Failed to delete notification");
      }
    } catch (error) {
      toast.error("Failed to delete notification");
      console.error("Error deleting notification:", error);
    }
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(notification => ({ ...notification, read: true })));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" />
        Loading notifications...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Notifications</h1>
          <p className="text-muted-foreground">Manage your notifications and preferences</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => {
              void loadNotifications();
            }}
            variant="outline"
            size="sm"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Badge variant="secondary" className="text-sm">
            {unreadCount} unread
          </Badge>
          <Button
            onClick={markAllAsRead}
            variant="outline"
            size="sm"
            style={{
              borderColor: currentTheme.colors.primary,
              color: currentTheme.colors.primary,
            }}
          >
            Mark all as read
          </Button>
        </div>
      </div>

      <Tabs defaultValue="all" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all">All ({notifications.length})</TabsTrigger>
          <TabsTrigger value="unread">Unread ({unreadCount})</TabsTrigger>
          <TabsTrigger value="system">System</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>All Notifications</CardTitle>
              <CardDescription>View and manage all your notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {notifications.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No notifications found</p>
                </div>
              ) : (
                notifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`flex items-start space-x-4 p-4 rounded-lg border ${
                      notification.read ? "bg-background" : "bg-muted/50"
                    }`}
                  >
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <p
                            className={`text-sm font-medium ${
                              notification.read ? "text-muted-foreground" : "text-foreground"
                            }`}
                          >
                            {notification.title}
                          </p>
                          <Badge className={`text-xs ${getNotificationBadge(notification.type)}`}>
                            {notification.category}
                          </Badge>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-muted-foreground flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {notification.timestamp}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              deleteNotification(notification.id);
                            }}
                            className="h-6 w-6 p-0"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <p
                        className={`text-sm mt-1 ${
                          notification.read ? "text-muted-foreground" : "text-foreground"
                        }`}
                      >
                        {notification.message}
                      </p>
                      {!notification.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            markAsRead(notification.id);
                          }}
                          className="mt-2 h-6 text-xs"
                          style={{ color: currentTheme.colors.primary }}
                        >
                          Mark as read
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="unread" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Unread Notifications</CardTitle>
              <CardDescription>Notifications that require your attention</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {notifications.filter(n => !n.read).length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <p className="text-muted-foreground">All notifications have been read</p>
                </div>
              ) : (
                notifications
                  .filter(notification => !notification.read)
                  .map(notification => (
                    <div
                      key={notification.id}
                      className="flex items-start space-x-4 p-4 rounded-lg border bg-muted/50"
                    >
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-foreground">
                              {notification.title}
                            </p>
                            <Badge className={`text-xs ${getNotificationBadge(notification.type)}`}>
                              {notification.category}
                            </Badge>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-muted-foreground flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {notification.timestamp}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                deleteNotification(notification.id);
                              }}
                              className="h-6 w-6 p-0"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm mt-1 text-foreground">{notification.message}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            markAsRead(notification.id);
                          }}
                          className="mt-2 h-6 text-xs"
                          style={{ color: currentTheme.colors.primary }}
                        >
                          Mark as read
                        </Button>
                      </div>
                    </div>
                  ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="system" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>System Notifications</CardTitle>
              <CardDescription>Important system updates and alerts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {notifications.filter(n => n.category === "system").length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No system notifications</p>
                </div>
              ) : (
                notifications
                  .filter(notification => notification.category === "system")
                  .map(notification => (
                    <div
                      key={notification.id}
                      className={`flex items-start space-x-4 p-4 rounded-lg border ${
                        notification.read ? "bg-background" : "bg-muted/50"
                      }`}
                    >
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <p
                              className={`text-sm font-medium ${
                                notification.read ? "text-muted-foreground" : "text-foreground"
                              }`}
                            >
                              {notification.title}
                            </p>
                            <Badge className={`text-xs ${getNotificationBadge(notification.type)}`}>
                              {notification.category}
                            </Badge>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-muted-foreground flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {notification.timestamp}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                deleteNotification(notification.id);
                              }}
                              className="h-6 w-6 p-0"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <p
                          className={`text-sm mt-1 ${
                            notification.read ? "text-muted-foreground" : "text-foreground"
                          }`}
                        >
                          {notification.message}
                        </p>
                        {!notification.read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              markAsRead(notification.id);
                            }}
                            className="mt-2 h-6 text-xs"
                            style={{ color: currentTheme.colors.primary }}
                          >
                            Mark as read
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Configure how you receive notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Notification Channels</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Email Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Receive notifications via email
                      </p>
                    </div>
                    <Switch
                      checked={notificationSettings.email}
                      onCheckedChange={checked => {
                        setNotificationSettings(prev => ({ ...prev, email: checked }));
                      }}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Push Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Receive push notifications in browser
                      </p>
                    </div>
                    <Switch
                      checked={notificationSettings.push}
                      onCheckedChange={checked => {
                        setNotificationSettings(prev => ({ ...prev, push: checked }));
                      }}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">SMS Notifications</Label>
                      <p className="text-sm text-muted-foreground">Receive notifications via SMS</p>
                    </div>
                    <Switch
                      checked={notificationSettings.sms}
                      onCheckedChange={checked => {
                        setNotificationSettings(prev => ({ ...prev, sms: checked }));
                      }}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">Notification Types</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">System Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        System updates and maintenance alerts
                      </p>
                    </div>
                    <Switch
                      checked={notificationSettings.system}
                      onCheckedChange={checked => {
                        setNotificationSettings(prev => ({ ...prev, system: checked }));
                      }}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">User Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        User registration and activity updates
                      </p>
                    </div>
                    <Switch
                      checked={notificationSettings.user}
                      onCheckedChange={checked => {
                        setNotificationSettings(prev => ({ ...prev, user: checked }));
                      }}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Admin Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Administrative and payment updates
                      </p>
                    </div>
                    <Switch
                      checked={notificationSettings.admin}
                      onCheckedChange={checked => {
                        setNotificationSettings(prev => ({ ...prev, admin: checked }));
                      }}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-base">Alert Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Critical alerts and error notifications
                      </p>
                    </div>
                    <Switch
                      checked={notificationSettings.alert}
                      onCheckedChange={checked => {
                        setNotificationSettings(prev => ({ ...prev, alert: checked }));
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setNotificationSettings(defaultSettings);
                    toast.info("Settings reset to default");
                  }}
                  disabled={settingsLoading}
                >
                  Reset to Default
                </Button>
                <Button
                  style={{ backgroundColor: currentTheme.colors.primary }}
                  onClick={async () => {
                    setSettingsLoading(true);
                    try {
                      const response = await apiService.updateNotificationPreferences({
                        notifications: notificationSettings,
                      });
                      if (response.type === 0) {
                        toast.success("Notification preferences saved successfully");
                      } else {
                        toast.error(response.message || "Failed to save preferences");
                      }
                    } catch (error) {
                      console.error("Error saving preferences:", error);
                      toast.error("Failed to save preferences");
                    } finally {
                      setSettingsLoading(false);
                    }
                  }}
                  disabled={settingsLoading}
                >
                  {settingsLoading ? "Saving..." : "Save Preferences"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
